import { useCallback, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { calculateCalendarPosition } from '../lib/calendarPosition';

export const useCalendarPosition = ({ calendarRef, isOpen, onClose, position = 'left' }) => {
  const { pathname } = useLocation();

  const setCalendarFixedPosition = useCallback(() => {
    if (pathname === '/iframe.html') return;

    const calendarContainer = calendarRef?.current?.parentNode;
    const buttonRef = calendarRef?.current?.closest('#date-input-picker');
    if (calendarContainer && buttonRef) {
      const buttonRect = buttonRef.getBoundingClientRect();

      // Get calendar dimensions - use defaults if not available yet
      let calendarHeight = calendarContainer.offsetHeight;
      let calendarWidth = calendarContainer.offsetWidth;

      // If dimensions are 0 or not available, try to get them from getBoundingClientRect
      if (!calendarHeight || !calendarWidth) {
        const calendarRect = calendarContainer.getBoundingClientRect();
        calendarHeight = calendarRect.height || 300;
        calendarWidth = calendarRect.width || 300;
      }

      // Calculate transform container offset (for modals with transform CSS)
      // Set to 0,0 first to measure the actual offset
      calendarContainer.style.cssText = `
        position: fixed;
        left: 0px;
        top: 0px;
        visibility: hidden;
      `;
      const testRect = calendarContainer.getBoundingClientRect();
      const offsetLeft = testRect.left;
      const offsetTop = testRect.top;

      // For position: fixed, coordinates are relative to viewport
      // Calculate available space
      const availableWidth = window.innerWidth;
      const availableHeight = window.innerHeight;
      // documentRectLeft is 0 for fixed positioning (viewport-relative)
      const documentRectLeft = 0;

      // Calculate position using the lib function
      let { left, top } = calculateCalendarPosition({
        buttonRect,
        calendarWidth,
        calendarHeight,
        availableWidth,
        availableHeight,
        documentRectLeft,
        position
      });

      // Adjust for transform container offset
      left = left - offsetLeft;
      top = top - offsetTop;

      calendarContainer.style.cssText = `
        position: fixed;
        left: ${left}px;
        top: ${top}px;
        opacity: 0;
        z-index: 1000;
      `;

      requestAnimationFrame(() => {
        calendarContainer.style.cssText = `
          position: fixed;
          left: ${left}px;
          top: ${top}px;
          opacity: 1;
          z-index: 1000;
        `;
      });
    }
  }, [calendarRef, position]);

  useLayoutEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setCalendarFixedPosition();
      }, 200);

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const container = calendarRef?.current?.parentNode;
            if (container && container.style.position !== 'fixed') {
              setCalendarFixedPosition();
            }
          }
        });
      });

      const container = calendarRef?.current?.parentNode;
      if (container) observer.observe(container, { attributes: true });

      window.addEventListener('resize', onClose, true);
      window.addEventListener('scroll', onClose, true);

      return () => {
        observer.disconnect();
        window.removeEventListener('resize', onClose, true);
        window.removeEventListener('scroll', onClose, true);
      };
    }
  }, [isOpen, setCalendarFixedPosition, onClose]);
};
